import React, { useEffect, useState, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, Link, NavLink } from "react-router-dom";
import axios from "axios";
import "@/App.css";

import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import DashboardLayout from "./pages/DashboardLayout";
import Home from "./pages/Home";
import MyPets from "./pages/MyPets";
import Vets from "./pages/Vets";
import Appointments from "./pages/Appointments";
import Store from "./pages/Store";
import Adoption from "./pages/Adoption";
import Meetups from "./pages/Meetups";
import Hostels from "./pages/Hostels";
import Feed from "./pages/Feed";
import SymptomChecker from "./pages/SymptomChecker";
import Refer from "./pages/Refer";
import Partner from "./pages/Partner";
import VetDashboard from "./pages/VetDashboard";
import StoreDashboard from "./pages/StoreDashboard";
import HostelDashboard from "./pages/HostelDashboard";
import Admin from "./pages/Admin";
import Ngos from "./pages/Ngos";
import Foster from "./pages/Foster";
import LostPets from "./pages/LostPets";
import Tracking from "./pages/Tracking";
import PetId from "./pages/PetId";
import Scan from "./pages/Scan";
import PaymentReturn from "./pages/PaymentReturn";
import VetMedicines from "./pages/VetMedicines";
import VetTemplate from "./pages/VetTemplate";
import VetPatients from "./pages/VetPatients";
import VetPrescribe from "./pages/VetPrescribe";
import PrescriptionView from "./pages/PrescriptionView";
import PetMedicalHistory from "./pages/PetMedicalHistory";
import PetPoster from "./pages/PetPoster";
import PetCollar from "./pages/PetCollar";
import Billing from "./pages/Billing";
import Kyc from "./pages/Kyc";
import RescueCases from "./pages/RescueCases";
import NgoRescueCases from "./pages/NgoRescueCases";
import NgoFosterApps from "./pages/NgoFosterApps";
import NgoImpact from "./pages/NgoImpact";
import VetAvailability from "./pages/VetAvailability";
import VetInventory from "./pages/VetInventory";
import VetProBono from "./pages/VetProBono";
import NgoRegister from "./pages/NgoRegister";
import AdminVideos from "./pages/AdminVideos";
import AdminPlans from "./pages/AdminPlans";
import AdminDeck from "./pages/AdminDeck";
import AdminInsurance from "./pages/AdminInsurance";
import AdminAnalytics from "./pages/AdminAnalytics";
import Insurance from "./pages/Insurance";
import PrivacyData from "./pages/PrivacyData";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Refund from "./pages/Refund";
import Cancellation from "./pages/Cancellation";
import FoundPet from "./pages/FoundPet";
import PartnerCatalog from "./pages/PartnerCatalog";
import Services from "./pages/Services";
import ServicesDashboard from "./pages/ServicesDashboard";
import VanSchedule from "./pages/VanSchedule";
import Earnings from "./pages/Earnings";
import AdminPayments from "./pages/AdminPayments";
import VetVisits from "./pages/VetVisits";
import HostelBookings from "./pages/HostelBookings";
import StoreOrders from "./pages/StoreOrders";
import MyOrders from "./pages/MyOrders";
import VendorProfile from "./pages/VendorProfile";
import NgoDonations from "./pages/NgoDonations";
import PetRegistration from "./pages/PetRegistration";
import AdminPetRegistrations from "./pages/AdminPetRegistrations";
import PayoutSetup from "./pages/PayoutSetup";
import AdminFinancials from "./pages/AdminFinancials";
import InstallPrompt from "./components/InstallPrompt";
import { Toaster } from "./components/ui/sonner";
import { I18nProvider } from "./i18n";
import { initAnalytics, trackPageView } from "./lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// ---------- Auth Context ----------
const AuthCtx = createContext({ user: null, loading: true, refresh: () => {}, logout: () => {} });
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, let AuthCallback exchange session first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) {}
    // Flush all caches + unregister any old service workers so users on a
    // post-deploy stale shell don't get blank screens. Safe on browsers
    // that don't support these APIs (eg. older Safari).
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch {}
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.update().catch(() => null)));
      }
    } catch {}
    try { localStorage.removeItem("petbond_post_signin"); } catch {}
    setUser(null);
    window.location.href = "/";
  }, []);

  return <AuthCtx.Provider value={{ user, loading, refresh, logout }}>{children}</AuthCtx.Provider>;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-ink-soft font-heading text-xl">Fetching your pack…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Fire page_view on every route change (skip during OAuth exchange to avoid noise)
  useEffect(() => {
    if (!location.hash?.includes("session_id=")) {
      trackPageView(location.pathname);
    }
  }, [location.pathname]);
  // Process OAuth callback BEFORE normal routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/cancellation" element={<Cancellation />} />
      <Route path="/found/:petId" element={<FoundPet />} />
      <Route path="/auth/google" element={<GoogleAuthCallback />} />
      <Route path="/scan/:petId" element={<Scan />} />
      <Route path="/payment-return" element={<PaymentReturn />} />
      <Route
        path="/app"
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<Home />} />
        <Route path="pets" element={<MyPets />} />
        <Route path="vets" element={<Vets />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="store" element={<Store />} />
        <Route path="adoption" element={<Adoption />} />
        <Route path="meetups" element={<Meetups />} />
        <Route path="hostels" element={<Hostels />} />
        <Route path="feed" element={<Feed />} />
        <Route path="symptom-checker" element={<SymptomChecker />} />
        <Route path="refer" element={<Refer />} />
        <Route path="partner" element={<Partner />} />
        <Route path="vet-dashboard" element={<VetDashboard />} />
        <Route path="store-dashboard" element={<StoreDashboard />} />
        <Route path="hostel-dashboard" element={<HostelDashboard />} />
        <Route path="admin" element={<Admin />} />
        <Route path="ngos" element={<Ngos />} />
        <Route path="foster" element={<Foster />} />
        <Route path="lost-pets" element={<LostPets />} />
        <Route path="tracking" element={<Tracking />} />
        <Route path="pet-id" element={<PetId />} />
        <Route path="vet/medicines" element={<VetMedicines />} />
        <Route path="vet/template" element={<VetTemplate />} />
        <Route path="vet/patients" element={<VetPatients />} />
        <Route path="vet/prescribe/:petId" element={<VetPrescribe />} />
        <Route path="prescription/:petId/:prescriptionId" element={<PrescriptionView />} />
        <Route path="pets/:petId/medical-history" element={<PetMedicalHistory />} />
        <Route path="pets/:petId/poster" element={<PetPoster />} />
        <Route path="pets/:petId/collar" element={<PetCollar />} />
        <Route path="billing" element={<Billing />} />
        <Route path="kyc" element={<Kyc />} />
        <Route path="rescue-cases" element={<RescueCases />} />
        <Route path="ngo/rescue-cases" element={<NgoRescueCases />} />
        <Route path="ngo/foster-applications" element={<NgoFosterApps />} />
        <Route path="ngo/impact" element={<NgoImpact />} />
        <Route path="vet/availability" element={<VetAvailability />} />
        <Route path="vet/inventory" element={<VetInventory />} />
        <Route path="vet/pro-bono" element={<VetProBono />} />
        <Route path="ngo/register" element={<NgoRegister />} />
        <Route path="admin/videos" element={<AdminVideos />} />
        <Route path="store/products" element={<PartnerCatalog mode="store" />} />
        <Route path="hostel/rooms" element={<PartnerCatalog mode="hostel" />} />
        <Route path="services" element={<Services />} />
        <Route path="services-dashboard" element={<ServicesDashboard />} />
        <Route path="van-schedule" element={<VanSchedule />} />
        <Route path="earnings" element={<Earnings />} />
        <Route path="admin/payments" element={<AdminPayments />} />
        <Route path="vet/visits" element={<VetVisits />} />
        <Route path="hostel/bookings" element={<HostelBookings />} />
        <Route path="store/orders" element={<StoreOrders />} />
        <Route path="my-orders" element={<MyOrders />} />
        <Route path="profile" element={<VendorProfile />} />
        <Route path="ngo/donations" element={<NgoDonations />} />
        <Route path="pet-registration" element={<PetRegistration />} />
        <Route path="admin/pet-registrations" element={<AdminPetRegistrations />} />
        <Route path="payout-setup" element={<PayoutSetup />} />
        <Route path="admin/financials" element={<AdminFinancials />} />
        <Route path="admin/plans" element={<AdminPlans />} />
        <Route path="admin/deck" element={<AdminDeck />} />
        <Route path="admin/insurance" element={<AdminInsurance />} />
        <Route path="admin/analytics" element={<AdminAnalytics />} />
        <Route path="insurance" element={<Insurance />} />
        <Route path="privacy-data" element={<PrivacyData />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    initAnalytics();
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/service-worker.js").catch(() => {});
      });
    }
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <AppRouter />
            <InstallPrompt />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}
